import React, { useEffect, useRef, useState } from "react";

/**
 * Tween between the previous `value` and the incoming `value` over
 * `duration` ms (default 200). Respects `prefers-reduced-motion`. Uses
 * requestAnimationFrame; zero deps.
 *
 * Props:
 *   value      — target numeric value
 *   decimals   — digits after decimal (default: auto — 0 if |value|>=100, 1 if >=10, else 2)
 *   duration   — ms (default 200)
 *   prefix     — string shown before the number (rarely needed — prefer Metric component)
 *   suffix     — string shown after (rarely needed)
 *   className  — forwarded to the outer span
 */
export default function CountUp({
    value,
    decimals,
    duration = 200,
    prefix = "",
    suffix = "",
    className = "",
    "data-testid": testId,
}) {
    const safe = Number.isFinite(+value) ? +value : 0;
    const [display, setDisplay] = useState(safe);
    const fromRef = useRef(safe);
    const rafRef = useRef(0);
    const reduced = useRef(
        typeof window !== "undefined"
            && window.matchMedia
            && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );

    useEffect(() => {
        cancelAnimationFrame(rafRef.current);
        const from = fromRef.current;
        const to = safe;
        if (reduced.current || from === to || duration <= 0) {
            setDisplay(to);
            fromRef.current = to;
            return;
        }
        const start = performance.now();
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // ease-out cubic — snappy but smooth
            const eased = 1 - Math.pow(1 - t, 3);
            const current = from + (to - from) * eased;
            setDisplay(current);
            if (t < 1) rafRef.current = requestAnimationFrame(tick);
            else fromRef.current = to;
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [safe, duration]);

    const auto = decimals ?? (Math.abs(safe) >= 100 ? 0 : Math.abs(safe) >= 10 ? 1 : 2);
    const text = display.toFixed(auto);

    return (
        <span className={className} data-testid={testId}>
            {prefix}{text}{suffix}
        </span>
    );
}
